<script setup>
import { defineProps, ref, computed } from 'vue'
import Provider from '@/api/Provider.js'
import { useMainStore } from '@/stores/main'

const mainStore = useMainStore()

const props = defineProps({
  providerName: {
    type: String,
    required: true
  },
  providerDisplayName: {
    type: String,
    default: null
  },
  providerThumbnail: {
    type: String,
    default: null
  }
})

const items = ref('')
const description = ref('')
const category = ref(null)
const processing = ref(false)
const submitted = ref(false)
const failed = ref(false)
const errorMessage = ref('')
const results = ref(null)

async function submit() {
  processing.value = true
  submitted.value = false
  failed.value = false
  errorMessage.value = ''

  // Parse items (split by newlines, filter empty)
  const itemsList = items.value
    .split('\n')
    .map(item => item.trim())
    .filter(item => item !== '')

  if (itemsList.length === 0) {
    errorMessage.value = 'Please enter at least one IP, domain, or URL'
    failed.value = true
    processing.value = false
    return
  }

  const result = await Provider.submitProviderData(
    props.providerName,
    itemsList,
    description.value || null,
    category.value
  )

  if (result.status === 'success') {
    submitted.value = true
    results.value = result.data
    mainStore.event_emit('provider_data_submitted')

    // Clear form
    items.value = ''
    description.value = ''
  } else {
    failed.value = true
    errorMessage.value = result.message || 'Failed to submit data'
  }

  processing.value = false
}
</script>

<template>
  <div class="provider-slideover">
    <!-- Header -->
    <div class="box-header">
      <h3>Submit Provider Data</h3>
    </div>

    <!-- Content -->
    <div class="box-content">
      <!-- Success State -->
      <div v-if="submitted" class="success-state">
        <div class="success-icon">
          <font-awesome-icon :icon="['fas', 'check-circle']" />
        </div>
        <h4>Successfully Submitted!</h4>
        <p>
          <strong>{{ results.submitted_count }}</strong> {{ results.submitted_count === 1 ? 'item' : 'items' }} submitted successfully
          <span v-if="results.failed_count > 0">
            <br><strong>{{ results.failed_count }}</strong> {{ results.failed_count === 1 ? 'item' : 'items' }} failed validation
          </span>
        </p>

        <div v-if="results.results && results.results.length > 0" class="results-section">
          <h6 class="results-title">Details:</h6>
          <div class="results-list">
            <div
              v-for="(item, index) in results.results"
              :key="index"
              class="result-item"
              :class="{ 'success': item.status === 'success', 'error': item.status === 'invalid' }"
            >
              <span class="result-icon">
                <font-awesome-icon :icon="['fas', item.status === 'success' ? 'check' : 'times']" />
              </span>
              <span class="result-value">{{ item.value }}</span>
              <span v-if="item.status === 'success'" class="result-type">
                {{ item.item_type === 1 ? 'IP' : item.item_type === 2 ? 'Domain' : 'URL' }}
              </span>
              <span v-if="item.error_message" class="result-error">{{ item.error_message }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Form -->
      <form v-else class="provider-form" @submit.prevent="submit()">
        <div class="form-content">
          <!-- Error Message -->
          <div v-if="failed" class="alert alert-error mb-3">
            <font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
            <span>{{ errorMessage }}</span>
          </div>

          <!-- Provider Display (Disabled) -->
          <div class="form-group">
            <label>Provider</label>
            <div class="provider-display">
              <div class="provider-thumbnail">
                <img
                  v-if="providerThumbnail"
                  :src="providerThumbnail"
                  :alt="providerDisplayName || providerName"
                  class="thumbnail-image"
                />
                <div v-else class="thumbnail-placeholder">
                  <font-awesome-icon :icon="['fas', 'server']" />
                </div>
              </div>
              <div class="provider-info">
                <div class="provider-name">{{ providerDisplayName || providerName }}</div>
                <div v-if="providerDisplayName && providerDisplayName !== providerName" class="provider-subtitle">
                  {{ providerName }}
                </div>
              </div>
            </div>
          </div>

          <!-- Category Selection (Optional) -->
          <div class="form-group">
            <label for="category" class="form-label">Category (Optional)</label>
            <select
              id="category"
              v-model="category"
              class="form-select"
            >
              <option :value="null">-- Select Category --</option>
              <option :value="1">VPN</option>
              <option :value="2">Proxy</option>
              <option :value="3">Residential Proxy</option>
            </select>
          </div>

          <!-- Items Input -->
          <div class="form-group">
            <label for="items">IPs, Domains, or URLs *</label>
            <div class="input-wrapper">
              <textarea
                v-model="items"
                id="items"
                placeholder="Enter IPs, domains, or URLs (one per line)&#10;Example:&#10;192.168.1.1&#10;example.com&#10;https://example.org"
                rows="8"
                :disabled="processing"
                required
              ></textarea>
            </div>
            <div class="form-help">
              Enter one item per line. Supports IPs (IPv4/IPv6), domain names, and URLs.
            </div>
          </div>

          <!-- Description Input -->
          <div class="form-group">
            <label for="description">Description</label>
            <div class="input-wrapper">
              <textarea
                v-model="description"
                id="description"
                placeholder="Optional description for this submission"
                rows="3"
                :disabled="processing"
              ></textarea>
            </div>
            <div class="form-help">
              Add context or notes about these submissions (optional).
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="form-actions">
          <button
            type="submit"
            class="submit-btn"
            :disabled="processing || items.trim() === ''"
          >
            <font-awesome-icon v-if="processing" :icon="['fas', 'spinner']" spin />
            {{ processing ? 'Submitting...' : 'Submit Data' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.provider-slideover {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  color: white;
  margin: 0;
  padding: 0;
}

.box-header {
  background-color: rgba(255, 255, 255, 0.03);
  padding: 1rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  width: 100%;
  margin: 0;
  border-radius: 10px 0 0 0;
}

.box-header h3 {
  margin: 0;
  color: #E5E7EB;
  font-size: 1.1rem;
  font-weight: 500;
}

.box-content {
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
}

/* Success State */
.success-state {
  text-align: center;
  padding: 2rem 1rem;
}

.success-icon {
  font-size: 3rem;
  color: #10B981;
  margin-bottom: 1rem;
}

.success-state h4 {
  color: white;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.success-state p {
  color: #9CA3AF;
  margin: 0 0 1.5rem 0;
}

.results-section {
  margin-top: 2rem;
  text-align: left;
}

.results-title {
  color: #E5E7EB;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
}

.results-list {
  max-height: 300px;
  overflow-y: auto;
  font-size: 0.875rem;
}

.result-item {
  padding: 0.75rem;
  margin-bottom: 0.5rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background-color: #171C24;
  border: 1px solid #374151;
}

.result-item.success {
  border-left: 3px solid #10B981;
}

.result-item.error {
  border-left: 3px solid #EF4444;
}

.result-icon {
  min-width: 20px;
  text-align: center;
}

.result-item.success .result-icon {
  color: #10B981;
}

.result-item.error .result-icon {
  color: #EF4444;
}

.result-value {
  flex: 1;
  font-family: monospace;
  color: #E5E7EB;
}

.result-type {
  background-color: rgba(115, 87, 210, 0.2);
  color: #A78BFA;
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
}

.result-error {
  color: #FCA5A5;
  font-size: 0.75rem;
}

/* Form Styles */
.provider-form {
  max-width: none;
  width: 100%;
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  padding: 0;
}

.form-content {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #E5E7EB;
  font-weight: 500;
  font-size: 0.875rem;
}

.input-wrapper {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  transition: border-color 0.2s;
}

.input-wrapper:focus-within {
  border-color: #7357D2;
}

.input-wrapper input,
.input-wrapper textarea,
.input-wrapper select {
  background: transparent;
  border: none;
  outline: none;
  color: white;
  width: 100%;
  resize: none;
}

.input-wrapper input::placeholder,
.input-wrapper textarea::placeholder {
  color: #6B7280;
}

.form-select {
  background-color: #171C24;
  border: 1px solid #374151;
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: white;
  width: 100%;
  outline: none;
  transition: border-color 0.2s;
  cursor: pointer;
}

.form-select:focus {
  border-color: #7357D2;
}

.form-select option {
  background-color: #171C24;
  color: white;
}

.form-help {
  margin-top: 0.25rem;
  font-size: 0.75rem;
  color: #6B7280;
}

/* Alert */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert-error {
  background-color: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #FCA5A5;
}

.mb-3 {
  margin-bottom: 1rem;
}

/* Provider Display (Disabled Field) */
.provider-display {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background-color: #171C24;
  border-radius: 10px;
  border: 1px solid #374151;
  opacity: 0.8;
  cursor: not-allowed;
}

.provider-thumbnail {
  flex-shrink: 0;
}

.thumbnail-image {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.thumbnail-placeholder {
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #374151;
  border-radius: 8px;
  color: #6B7280;
  font-size: 1.25rem;
}

.provider-info {
  flex: 1;
}

.provider-name {
  color: #E5E7EB;
  font-weight: 500;
  font-size: 1rem;
}

.provider-subtitle {
  color: #6B7280;
  font-size: 0.875rem;
  margin-top: 0.125rem;
}

/* Form Actions */
.form-actions {
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  border-top: 1px solid #374151;
  text-align: center;
  background-color: #191f27;
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  width: 100%;
  box-sizing: border-box;
  margin-left: -2rem;
  margin-right: -2rem;
  margin-bottom: -2rem;
}

.submit-btn {
  padding: 0.75rem 2rem;
  background-color: #7357D2;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.submit-btn:hover:not(:disabled) {
  background-color: #7F62E3;
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Custom scrollbar */
.box-content::-webkit-scrollbar,
.form-content::-webkit-scrollbar,
.results-list::-webkit-scrollbar {
  width: 6px;
}

.box-content::-webkit-scrollbar-track,
.form-content::-webkit-scrollbar-track,
.results-list::-webkit-scrollbar-track {
  background: #1f2937;
}

.box-content::-webkit-scrollbar-thumb,
.form-content::-webkit-scrollbar-thumb,
.results-list::-webkit-scrollbar-thumb {
  background: #374151;
  border-radius: 3px;
}

.box-content::-webkit-scrollbar-thumb:hover,
.form-content::-webkit-scrollbar-thumb:hover,
.results-list::-webkit-scrollbar-thumb:hover {
  background: #4b5563;
}
</style>
